import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, addDoc, getDocs, query, where,
  getDoc, doc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, TextField,
  Button, IconButton, Alert, CircularProgress,
  Chip, Divider, ToggleButton, ToggleButtonGroup,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FeedbackIcon from '@mui/icons-material/Feedback';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from 'react-router-dom';

export const FEEDBACK_BLOCKS = [
  { value: 'allmant',     label: 'Allmänt' },
  { value: 'iup',         label: 'IUP' },
  { value: 'swotn',       label: 'SwotN' },
  { value: 'workshopn',   label: 'WorkshopN' },
  { value: 'budgetn',     label: 'BudgetN' },
  { value: 'dokumentn',   label: 'DokumentN' },
  { value: 'avgangstavla',label: 'Avgångstavla' },
  { value: 'offerter',    label: 'Offerter' },
  { value: 'anbud',       label: 'Anbud' },
  { value: 'crm',         label: 'CRM' },
  { value: 'medarbetare', label: 'Medarbetare / HR' },
  { value: 'applab',      label: 'AppLAB' },
  { value: 'mockupn',     label: 'MockupN' },
  { value: 'marketing',   label: 'Marketing / LinkedIn' },
  { value: 'pitchn',      label: 'PitchN' },
  { value: 'projekt',     label: 'Projekt' },
  { value: 'castn',       label: 'CastN' },
  { value: 'rekrytering', label: 'Rekrytering' },
];

const STATUS_CONFIG = {
  ny_kommentar: {
    label: 'Ny kommentar', color: '#F06292', text: 'white'
  },
  avvisad: {
    label: 'Avvisad', color: '#78909C', text: 'white'
  },
  har_inarbetats: {
    label: 'Har åtgärdats', color: '#66BB6A', text: 'white'
  },
  annat_forum: {
    label: 'Annat forum eller skede',
    color: '#283593', text: 'white'
  },
  ny_granskning: {
    label: 'Ny granskning', color: '#FFA726', text: 'white'
  },
  ska_inarbetas: {
    label: 'Ska åtgärdas', color: '#EF5350', text: 'white'
  },
};

export default function FeedbackPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState('feature');
  const [block, setBlock] = useState('allmant');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [myFeedback, setMyFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    async function load() {
      const userSnap = await getDoc(
        doc(db, 'users', currentUser.uid));
      if (userSnap.exists()) {
        setUserName(
          userSnap.data().name || currentUser.email || '');
      }
      await loadMyFeedback();
    }
    load();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMyFeedback() {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'feedback'),
          where('submittedBy', '==', currentUser.uid)
        )
      );
      const items = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) =>
          (b.createdAt?.toDate?.() || 0) -
          (a.createdAt?.toDate?.() || 0)
        );
      setMyFeedback(items);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await addDoc(collection(db, 'feedback'), {
        type,
        block,
        title: title.trim(),
        description: description.trim(),
        submittedBy: currentUser.uid,
        submittedByName: userName,
        submittedByEmail: currentUser.email,
        status: 'ny_kommentar',
        adminComment: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setTitle('');
      setDescription('');
      setType('feature');
      setBlock('allmant');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
      loadMyFeedback();
    } catch (err) {
      console.error(err);
      setError('Kunde inte skicka feedback. Forsok igen.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{
        backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton color="inherit"
            onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <FeedbackIcon />
          <Typography variant="h5" fontWeight="bold">
            Önskemål och Feedback
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3 }}>

        {/* Formulär */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Skicka in feedback
          </Typography>
          <Typography variant="body2" color="text.secondary"
            sx={{ mb: 2 }}>
            Har du ett önskemål på en ny funktion eller vill
            rapportera ett fel? Vi läser allt!
          </Typography>

          {submitted && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Tack! Din feedback har skickats in.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column',
            gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <ToggleButtonGroup value={type} exclusive size="small"
                onChange={(e, val) => { if (val) setType(val); }}>
                <ToggleButton value="feature">
                  <LightbulbIcon sx={{ mr: 1, fontSize: 18 }} />
                  Önskemål
                </ToggleButton>
                <ToggleButton value="bug">
                  <BugReportIcon sx={{ mr: 1, fontSize: 18 }} />
                  Felrapport
                </ToggleButton>
              </ToggleButtonGroup>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Gäller block</InputLabel>
                <Select value={block} label="Gäller block"
                  onChange={e => setBlock(e.target.value)}>
                  {FEEDBACK_BLOCKS.map(b => (
                    <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Rubrik"
              value={title}
              fullWidth
              placeholder={type === 'feature'
                ? 'Beskriv kortfattat vad du onskar...'
                : 'Vad fungerar inte?'}
              onChange={e => setTitle(e.target.value)}
            />

            <TextField
              label="Beskrivning"
              value={description}
              fullWidth multiline rows={4}
              placeholder={type === 'feature'
                ? 'Beskriv mer detaljerat vad du skulle vilja ha...'
                : 'Hur reproducerar man felet? Vad handde?'}
              onChange={e => setDescription(e.target.value)}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained"
                startIcon={submitting
                  ? <CircularProgress size={16} color="inherit" />
                  : <SendIcon />}
                onClick={handleSubmit}
                disabled={submitting || !title.trim() ||
                  !description.trim()}
                sx={{ bgcolor: '#FF6B00', color: 'white',
                  '&:hover': { bgcolor: '#e65c00' } }}>
                {submitting ? 'Skickar...' : 'Skicka feedback'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Mina tidigare inlägg */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Mina tidigare inlämningar
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center',
              py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : myFeedback.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Du har inte skickat in nagon feedback annu.
            </Typography>
          ) : (
            myFeedback.map(item => {
              const status = STATUS_CONFIG[item.status] ||
                STATUS_CONFIG.ny_kommentar;
              return (
                <Box key={item.id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex',
                        alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        {item.type === 'bug'
                          ? <BugReportIcon sx={{
                              fontSize: 16, color: '#EF5350'
                            }} />
                          : <LightbulbIcon sx={{
                              fontSize: 16, color: '#FFA726'
                            }} />
                        }
                        <Typography variant="subtitle2"
                          fontWeight="bold">
                          {item.title}
                        </Typography>
                        {item.block && item.block !== 'allmant' && (
                          <Chip label={FEEDBACK_BLOCKS.find(b => b.value === item.block)?.label || item.block}
                            size="small"
                            sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#ede7f6', color: '#4527a0' }} />
                        )}
                      </Box>
                      <Typography variant="body2"
                        color="text.secondary" sx={{ mb: 0.5 }}>
                        {item.description}
                      </Typography>
                      {item.adminComment && (
                        <Box sx={{
                          mt: 1, p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          borderLeft: '3px solid',
                          borderColor: status.color
                        }}>
                          <Typography variant="caption"
                            fontWeight="bold" color="text.secondary">
                            Svar fran teamet:
                          </Typography>
                          <Typography variant="body2"
                            sx={{ mt: 0.3 }}>
                            {item.adminComment}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block' }}>
                        {item.createdAt?.toDate?.()
                          ? item.createdAt.toDate()
                              .toLocaleDateString('sv-SE')
                          : ''}
                      </Typography>
                    </Box>
                    <Chip
                      label={status.label}
                      size="small"
                      sx={{
                        bgcolor: status.color,
                        color: status.text,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}
                    />
                  </Box>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              );
            })
          )}
        </Paper>
      </Container>
    </Box>
  );
}